import { useMemo } from 'react';
import type { RentHistogramPoint } from '../api/client';
import { formatCurrency } from '../utils/formatters';
import './RentHistogram.css';

interface RentHistogramProps {
  data: RentHistogramPoint[];
  title?: string;
  highlightPrice?: number;
  maxBars?: number;
}

export function RentHistogram({ data, title, highlightPrice, maxBars = 30 }: RentHistogramProps) {
  // Process and bucket the data
  const processedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Sort by price
    const sorted = [...data].sort((a, b) => a.price - b.price);

    // If too many bars, bucket them
    if (sorted.length > maxBars) {
      const bucketSize = Math.ceil(sorted.length / maxBars);
      const buckets: RentHistogramPoint[] = [];

      for (let i = 0; i < sorted.length; i += bucketSize) {
        const slice = sorted.slice(i, i + bucketSize);
        const avgPrice = Math.round(slice.reduce((sum, p) => sum + p.price, 0) / slice.length);
        const totalCount = slice.reduce((sum, p) => sum + p.count, 0);
        buckets.push({ price: avgPrice, count: totalCount });
      }
      return buckets;
    }

    return sorted;
  }, [data, maxBars]);

  const maxCount = useMemo(() => {
    return Math.max(...processedData.map(d => d.count), 1);
  }, [processedData]);

  const stats = useMemo(() => {
    if (!data || data.length === 0) return null;

    const totalListings = data.reduce((sum, d) => sum + d.count, 0);
    const weightedSum = data.reduce((sum, d) => sum + d.price * d.count, 0);
    const avgRent = Math.round(weightedSum / totalListings);

    // Find median price
    let cumulative = 0;
    const halfCount = totalListings / 2;
    let medianPrice = data[0]?.price || 0;
    for (const d of [...data].sort((a, b) => a.price - b.price)) {
      cumulative += d.count;
      if (cumulative >= halfCount) {
        medianPrice = d.price;
        break;
      }
    }

    const minPrice = Math.min(...data.map(d => d.price));
    const maxPrice = Math.max(...data.map(d => d.price));

    return { totalListings, avgRent, medianPrice, minPrice, maxPrice };
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <div className="rent-histogram empty">
        <p>No histogram data available</p>
      </div>
    );
  }

  return (
    <div className="rent-histogram">
      {title && <h4 className="histogram-title">{title}</h4>}

      {stats && (
        <div className="histogram-stats">
          <span className="stat">
            <strong>{stats.totalListings}</strong> listings
          </span>
          <span className="stat">
            Median: <strong>{formatCurrency(stats.medianPrice)}</strong>
          </span>
          <span className="stat">
            Avg: <strong>{formatCurrency(stats.avgRent)}</strong>
          </span>
          <span className="stat">
            Range: {formatCurrency(stats.minPrice)} - {formatCurrency(stats.maxPrice)}
          </span>
        </div>
      )}

      <div className="histogram-container">
        <div className="histogram-bars">
          {processedData.map((point, index) => {
            const heightPct = (point.count / maxCount) * 100;
            const isHighlighted = highlightPrice &&
              Math.abs(point.price - highlightPrice) < 100;

            return (
              <div
                key={index}
                className={`histogram-bar-wrapper ${isHighlighted ? 'highlighted' : ''}`}
                title={`${formatCurrency(point.price)}: ${point.count} listings`}
              >
                <div
                  className="histogram-bar"
                  style={{ height: `${heightPct}%` }}
                >
                  <span className="bar-count">{point.count}</span>
                </div>
                <span className="bar-label">
                  {formatCurrency(point.price).replace('$', '')}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
